import { Helmet } from 'react-helmet-async';

const SITE_ORIGIN = 'https://techieinteriors.co.ke';
const SITE_NAME = 'Techie Interiors';
const DEFAULT_TITLE = 'Interior Design Services in Nairobi, Kenya | Techie Interiors';
const DEFAULT_DESCRIPTION =
  'Techie Interiors provides residential and commercial interior solutions in Nairobi and across Kenya, including gypsum ceilings, TV wall units, kitchen cabinets, wardrobes, and fit-outs.';
const DEFAULT_IMAGE = `${SITE_ORIGIN}/techLogo.jpeg`;
const DEFAULT_IMAGE_ALT = 'Techie Interiors interior design project showcase';

/**
 * Normalise whitespace in text strings.
 */
const normalizeText = (value) => (value ? value.replace(/\s+/g, ' ').trim() : '');

/**
 * Convert a relative or absolute URL to the canonical absolute form.
 *
 * Rules (must stay in sync with .htaccess):
 *  - Always HTTPS
 *  - Always non-www
 *  - Root path  → https://techieinteriors.co.ke/  (trailing slash kept)
 *  - All other  → no trailing slash
 *  - All lowercase (URLs are case-sensitive on Linux hosts)
 */
const toCanonicalUrl = (value, fallback) => {
  let url = (value || fallback || '').trim();

  // Resolve relative URLs
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `${SITE_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  // Parse to manipulate cleanly
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return `${SITE_ORIGIN}/`;
  }

  // Enforce HTTPS
  parsed.protocol = 'https:';

  // Enforce non-www
  parsed.hostname = parsed.hostname.replace(/^www\./, '');

  // Lowercase the pathname (Linux file systems are case-sensitive)
  parsed.pathname = parsed.pathname.toLowerCase();

  // Strip query strings and hash from canonical
  parsed.search = '';
  parsed.hash = '';

  // Root path must end with /; all other paths must NOT end with /
  if (parsed.pathname === '' || parsed.pathname === '/') {
    parsed.pathname = '/';
  } else {
    parsed.pathname = parsed.pathname.replace(/\/+$/, '');
  }

  return parsed.toString();
};

/**
 * Build a BreadcrumbList schema from the canonical URL.
 * Returns null for the homepage (no breadcrumb needed).
 */
const buildBreadcrumb = (canonicalUrl) => {
  if (!canonicalUrl || canonicalUrl === `${SITE_ORIGIN}/`) return null;

  let parsed;
  try {
    parsed = new URL(canonicalUrl);
  } catch {
    return null;
  }

  const segments = parsed.pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${SITE_ORIGIN}/`,
    },
  ];

  let accumulated = SITE_ORIGIN;
  segments.forEach((seg, idx) => {
    accumulated += `/${seg}`;
    const name = seg
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    items.push({
      '@type': 'ListItem',
      position: idx + 2,
      name,
      item: accumulated,
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
};

/**
 * Central SEO component.
 *
 * Props:
 *  title          – page title (unique per page, ≤60 chars recommended)
 *  description    – meta description (unique per page, 120–160 chars)
 *  image          – OG/Twitter image URL (absolute or root-relative)
 *  imageAlt       – alt text for OG image
 *  url            – canonical URL for this page (absolute or root-relative)
 *  type           – OG type, defaults to "website"
 *  structuredData – single schema object or array of schema objects
 *  noindex        – set true to noindex/nofollow the page
 *  addBreadcrumb  – set false to suppress auto-generated BreadcrumbList schema
 *                   (e.g. when the page already supplies its own BreadcrumbList)
 */
const SEO = ({
  title,
  description,
  image,
  imageAlt = DEFAULT_IMAGE_ALT,
  url,
  type = 'website',
  structuredData = null,
  noindex = false,
  addBreadcrumb = true,
}) => {
  const canonicalUrl = toCanonicalUrl(url, SITE_ORIGIN);
  const canonicalImage = toCanonicalUrl(image || DEFAULT_IMAGE, DEFAULT_IMAGE);
  const finalTitle = normalizeText(title) || DEFAULT_TITLE;
  const finalDescription = normalizeText(description) || DEFAULT_DESCRIPTION;

  const robotsContent = noindex
    ? 'noindex, nofollow, noarchive'
    : 'index, follow, max-image-preview:large';

  // Normalise structuredData to an array, filtering nulls
  const schemaArray = (
    Array.isArray(structuredData)
      ? structuredData
      : structuredData
      ? [structuredData]
      : []
  ).filter(Boolean);

  // Check whether a BreadcrumbList is already present in the provided schemas
  const hasBreadcrumb = schemaArray.some(
    (s) => s['@type'] === 'BreadcrumbList'
  );

  // Auto-add BreadcrumbList only when the page hasn't provided one
  if (addBreadcrumb && !hasBreadcrumb) {
    const auto = buildBreadcrumb(canonicalUrl);
    if (auto) schemaArray.push(auto);
  }

  return (
    <Helmet prioritizeSeoTags>
      {/* Core */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="author" content={SITE_NAME} />
      <meta name="language" content="en-KE" />

      {/* Geo */}
      <meta name="geo.region" content="KE-30" />
      <meta name="geo.placename" content="Nairobi" />
      <meta name="ICBM" content="-1.286389,36.817223" />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={canonicalImage} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_KE" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={canonicalImage} />
      <meta name="twitter:image:alt" content={imageAlt} />
      <meta name="twitter:site" content="@techieinteriors" />
      <meta name="twitter:creator" content="@techieinteriors" />

      {/* Structured Data */}
      {schemaArray.map((schema, idx) => (
        <script
          key={`schema-${idx}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 0) }}
        />
      ))}
    </Helmet>
  );
};

export default SEO;
